import { Product, PromoAdvice } from '../types/product.types';
import { RM3_COMPLEX_PROMOTION } from '../utils/constants';
import appSdkClient from '../utils/sdk';

export const getComplexPromotion = async ({
  storeCodes,
  conditionFilter,
  version = 'v2',
  productIds,
  lang,
}: {
  storeCodes: string[];
  conditionFilter: string;
  version?: string;
  productIds: Array<{ shopifyProductId: string; price: string }>;
  lang: string;
}): Promise<PromoAdvice[]> => {
  try {
    const result = await appSdkClient.shop.product.getComplexPromotion({
      productIds: productIds,
      storeCodes,
      conditionFilter,
      lang,
      version,
    });

    let response: PromoAdvice[] = [];

    if (Array.isArray(result)) {
      response = result as unknown as PromoAdvice[];
    } else {
      console.error('Unexpected SDK response format:', result);
      return [];
    }

    return response;
  } catch (error) {
    console.error('Error in getComplexPromotion:', error);
    return [];
  }
};

/**
 * Extracts RM3 complex promotions from product collections array
 * Maps the collections data where type === "RM3_COMPLEX_PROMOTION" to PromoAdvice format
 * This replaces the previous method of extracting from variants.complexPromotion3P
 * @param products - Array of products from product API response
 * @returns Array of PromoAdvice objects containing RM3 complex promotions
 */
export const extractComplexPromotion3PFromProducts = (products: Product[]): PromoAdvice[] => {
  try {
    const promoAdvices: PromoAdvice[] = [];

    products.forEach((product) => {
      // Extract RM3 promotions from collections array
      if (product.collections && Array.isArray(product.collections)) {
        product.collections.forEach((collection) => {
          // Check if this is an RM3 complex promotion collection
          if ((collection.type as string) === RM3_COMPLEX_PROMOTION) {
            const promotion = (collection as any).promotion;
            const subTypeLabel = promotion?.subTypeLabel || RM3_COMPLEX_PROMOTION;

            // Convert Collection to PromoAdvice format
            const promoAdvice: PromoAdvice = {
              itemId: product.makroId || product.id.replace('gid://shopify/Product/', ''),
              promoId: collection.id,
              title: collection.promotion.title || '',
              titleAlt: collection.promotion?.titleTh,
              description: collection.description || '',
              startDate: collection.startDate || '',
              endDate: collection.endDate || '',
              type: RM3_COMPLEX_PROMOTION,
              subType: subTypeLabel,
              meta: {
                promoType: RM3_COMPLEX_PROMOTION,
                promoAdviseDesc: collection.title,
                conditionType: promotion?.conditionType || '',
                targetType: promotion?.conditionDetail?.targetType || 0,
                targetValue: promotion?.conditionDetail?.targetValue || 0,
                list: [],
                rewardType: promotion?.conditionDetail?.rewardType || 0,
                rewardValue: promotion?.conditionDetail?.rewardValue || 0,
                maxPerInvoice: promotion?.maxCap || 0,
                maxPerCustomer: 0,
                isTargeted: 'false',
              },
            };
            promoAdvices.push(promoAdvice);
          }
        });
      }
    });

    return promoAdvices;
  } catch (error) {
    console.error('Error extracting RM3 complex promotions from collections:', error);
    return [];
  }
};
